<template>
  <modal id="endDeviceForm"
         v-model="visible"
         :title="$t('a-endDevicesForm:createEndDevice')"
         no-footer
         size="lg"
         @modal:cancel="cancel"
  >
    <small slot="modal-header-right" class="text-muted font-italic">{{ $t('a-endDevicesForm:required') | capitalize }}</small>

    <div slot="modal-body">
      <stepper ref="stepper" v-model="step" :steps="steps" :on-next="next" />

      <template v-if="step === 0">
        <div class="form-row">
          <div class="form-group col-lg-6">
            <label for="clusterId" class="control-label">{{ $t('a-endDevicesForm:cluster') | capitalize }} *</label>
            <select id="clusterId"
                    key="clusterId"
                    v-model="clusterId"
                    v-validate="'required'"
                    :data-vv-as="$t('a-endDevicesForm:cluster')"
                    :class="{ 'is-invalid': errors.has('clusterId') }"
                    name="clusterId"
                    class="form-control"
            >
              <template v-if="isRole.superAdmin">
                <optgroup v-for="(clusters, customer) in groupedClusters" :key="customer" :label="customer">
                  <option v-for="cluster in clusters" :key="cluster.id" :value="cluster.id">{{ cluster.name }}</option>
                </optgroup>
              </template>

              <template v-else>
                <option v-for="cluster in $store.getters.allClusters" :key="cluster.id" :value="cluster.id">{{ cluster.name }}</option>
              </template>
            </select>
            <div class="invalid-feedback">{{ errors.first('clusterId') }}</div>
          </div>

          <div class="form-group col-lg-6">
            <label for="name" class="control-label">{{ $t('a-endDevicesForm:name') | capitalize }}</label>
            <input id="name"
                   v-model="name"
                   v-validate="'text'"
                   :data-vv-as="$t('a-endDevicesForm:name')"
                   :class="{ 'is-invalid': errors.has('name') }"
                   type="text"
                   name="name"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('name') }}</div>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-lg-4">
            <label for="profile" class="control-label">{{ $t('a-endDevicesForm:profile') | capitalize }} *</label>
            <select id="profile"
                    key="profile"
                    v-model="profile"
                    v-validate="'required'"
                    :data-vv-as="$t('a-endDevicesForm:profile')"
                    :class="{ 'is-invalid': errors.has('profile') }"
                    name="profile"
                    class="form-control"
            >
              <option value="STATIC">{{ $t('endDevices:profiles.STATIC') | capitalize }}</option>
              <option value="WALKING">{{ $t('endDevices:profiles.WALKING') | capitalize }}</option>
              <option value="VEHICLE">{{ $t('endDevices:profiles.VEHICLE') | capitalize }}</option>
            </select>
            <div class="invalid-feedback">{{ errors.first('profile') }}</div>
          </div>

          <div class="form-group col-lg-3 offset-lg-1">
            <label for="classType" class="control-label d-block">{{ $t('a-endDevicesForm:classType') | capitalize }} *</label>
            <input id="classTypeA"
                   v-model="classType"
                   v-validate="'required'"
                   :data-vv-as="$t('a-endDevicesForm:classType')"
                   :class="{ 'is-invalid': errors.has('classType') }"
                   type="radio"
                   value="A"
                   name="classType"
                   class="form-control"
            >
            <label for="classTypeA" class="control-label text-uppercase">A</label>

            <input id="classTypeB"
                   v-model="classType"
                   v-validate="'required'"
                   :data-vv-as="$t('a-endDevicesForm:classType')"
                   :class="{ 'is-invalid': errors.has('classType') }"
                   type="radio"
                   value="B"
                   name="classType"
                   class="form-control"
            >
            <label for="classTypeB" class="control-label text-uppercase">B</label>

            <input id="classTypeC"
                   v-model="classType"
                   type="radio"
                   value="C"
                   name="classType"
                   class="form-control"
                   @click="rxWindows=(rxWindows==='RX1'?'':rxWindows)"
            >
            <label for="classTypeC" class="control-label text-uppercase">C</label>

            <div class="invalid-feedback">{{ errors.first('classType') }}</div>
          </div>

          <div class="form-group col-lg-2 offset-lg-1">
            <label class="control-label d-block">{{ $t('a-endDevicesForm:adr') | capitalize }}</label>
            <input id="adrEnabled"
                   v-model="adrEnabled"
                   type="checkbox"
            >
            <label for="adrEnabled" />
            <label for="adrEnabled" class="control-label">{{ $t('a-endDevicesForm:adrEnable') | capitalize }}</label>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group" :class="countries.length > 0 ? 'col-lg-3' : 'col-lg-4'">
            <label for="rfRegion" class="control-label">{{ $t('a-endDevicesForm:rfRegion') | capitalize }} *</label>
            <select id="rfRegion"
                    key="rfRegion"
                    v-model="rfRegion"
                    v-validate="'required'"
                    :data-vv-as="$t('a-endDevicesForm:rfRegion')"
                    :class="{ 'is-invalid': errors.has('rfRegion') }"
                    name="rfRegion"
                    class="form-control"
            >
              <option v-for="region in regions" :key="region" :value="region">{{ region }}</option>
            </select>
            <div class="invalid-feedback">{{ errors.first('rfRegion') }}</div>
          </div>

          <div v-show="countries.length > 0" class="form-group col-lg-3">
            <label for="country" class="control-label">{{ $t('a-endDevicesForm:country') | capitalize }}</label>
            <select id="country"
                    v-model="country"
                    name="country"
                    class="form-control"
            >
              <option />
              <option v-for="c in countries" :key="c" :value="c">{{ $t('countries:' + c) }}</option>
            </select>
          </div>

          <div class="form-group" :class="countries.length > 0 ? 'col-lg-3' : 'col-lg-4'">
            <label for="macVersion" class="control-label">{{ $t('a-endDevicesForm:macVersion') | capitalize }} *</label>
            <select id="macVersion"
                    key="macVersion"
                    v-model="macVersion"
                    v-validate="'required'"
                    :data-vv-as="$t('a-endDevicesForm:macVersion')"
                    :class="{ 'is-invalid': errors.has('macVersion') }"
                    :disabled="macVersions.length===0"
                    name="macVersion"
                    class="form-control"
            >
              <option v-for="mv in macVersions" :key="mv" :value="mv">{{ mv }}</option>
            </select>
            <div class="invalid-feedback">{{ errors.first('macVersion') }}</div>
          </div>

          <div class="form-group" :class="countries.length > 0 ? 'col-lg-3' : 'col-lg-4'">
            <label for="regParamsRevision" class="control-label">{{ $t('a-endDevicesForm:regParamsRevision') | capitalize }}<span v-if="macVersion!=='1.0.1'"> *</span></label>
            <select id="regParamsRevision"
                    key="regParamsRevision"
                    v-model="regParamsRevision"
                    v-validate="{ rules: { required: macVersion!=='1.0.1' }}"
                    :data-vv-as="$t('a-endDevicesForm:regParamsRevision')"
                    :class="{ 'is-invalid': errors.has('regParamsRevision') }"
                    :disabled="regionalParameterRevisions.length===0"
                    name="regParamsRevision"
                    class="form-control"
            >
              <option v-for="regionalParameterRevision in regionalParameterRevisions" :key="regionalParameterRevision" :value="regionalParameterRevision">{{ regionalParameterRevision }}</option>
            </select>
            <div class="invalid-feedback">{{ errors.first('regParamsRevision') }}</div>
            <small v-if="macVersion==='1.0.1'" class="form-text text-muted">{{ $t('a-endDevicesForm:noRevFor101') | capitalize }}</small>
          </div>
        </div>
      </template>

      <template v-if="step === 1">
        <h3 class="mt-3">{{ $t('a-endDevicesForm:activation') | capitalize }}</h3>

        <div class="form-row">
          <div class="form-group col-lg-12">
            <label for="activation" class="control-label d-block">{{ $t('a-endDevicesForm:type') | capitalize }} *</label>
            <input id="otaa"
                   v-model="activation"
                   v-validate="'required'"
                   :data-vv-as="$t('a-endDevicesForm:type')"
                   :class="{ 'is-invalid': errors.has('activation') }"
                   type="radio"
                   value="OTAA"
                   name="activation"
                   class="form-control"
            >
            <label for="otaa" class="control-label">{{ $t('a-endDevicesForm:otaa') | capitalize }}</label>

            <input id="abp"
                   v-model="activation"
                   :class="{ 'is-invalid': errors.has('activation') }"
                   type="radio"
                   value="ABP"
                   name="activation"
                   class="form-control"
            >
            <label for="abp" class="control-label">{{ $t('a-endDevicesForm:abp') | capitalize }}</label>

            <div class="invalid-feedback">{{ errors.first('activation') }}</div>
          </div>
        </div>

        <div v-show="activation==='OTAA'" class="form-row">
          <div class="form-group col-lg-6">
            <label for="otaaDevEui" class="control-label">{{ $t('a-endDevicesForm:devEui') | capitalize }} *</label>
            <input id="otaaDevEui"
                   key="otaaDevEui"
                   v-model="devEui"
                   v-validate="{ rules: { required: activation==='OTAA', hexa16: true} }"
                   :data-vv-as="$t('a-endDevicesForm:devEui')"
                   :class="{ 'is-invalid': errors.has('otaaDevEui') }"
                   type="text"
                   name="otaaDevEui"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('otaaDevEui') }}</div>
          </div>

          <div class="form-group col-lg-6">
            <label for="appEui" class="control-label">{{ $t('a-endDevicesForm:appEui') | capitalize }} *</label>
            <input id="appEui"
                   key="appEui"
                   v-model="appEui"
                   v-validate="{ rules: { required: activation==='OTAA', hexa16: true} }"
                   :data-vv-as="$t('a-endDevicesForm:appEui')"
                   :class="{ 'is-invalid': errors.has('appEui') }"
                   type="text"
                   name="appEui"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('appEui') }}</div>
          </div>
        </div>

        <div v-show="activation==='OTAA'" class="form-row">
          <div class="form-group col-lg-12">
            <label for="appKey" class="control-label">{{ $t('a-endDevicesForm:appKey') | capitalize }}</label>
            <input id="appKey"
                   key="appKey"
                   v-model="appKey"
                   v-validate="{ rules: { hexa32: true} }"
                   :data-vv-as="$t('a-endDevicesForm:appKey')"
                   :class="{ 'is-invalid': errors.has('appKey') }"
                   type="text"
                   name="appKey"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('appKey') }}</div>
          </div>
        </div>

        <div v-show="activation==='OTAA' && isLoRaWan10" class="form-row">
          <div class="form-group col-lg-3">
            <label class="control-label d-block">{{ $t('a-endDevicesForm:devNonceCounter') | capitalize }}</label>
            <input id="devNonceCounter"
                   v-model="devNonceCounter"
                   type="checkbox"
            >
            <label for="devNonceCounter" />
            <label for="devNonceCounter" class="control-label">{{ $t('a-endDevicesForm:devNonceCounterEnable') | capitalize }}</label>
          </div>
        </div>

        <div v-show="activation==='ABP'" class="form-row">
          <div class="form-group col-lg-6">
            <label for="abpDevEui" class="control-label">{{ $t('a-endDevicesForm:devEui') | capitalize }} *</label>
            <input id="abpDevEui"
                   key="abpDevEui"
                   v-model="devEui"
                   v-validate="{ rules: { required: activation==='ABP', hexa16: true} }"
                   :data-vv-as="$t('a-endDevicesForm:devEui')"
                   :class="{ 'is-invalid': errors.has('abpDevEui') }"
                   type="text"
                   name="abpDevEui"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('abpDevEui') }}</div>
          </div>

          <div class="form-group col-lg-6">
            <label for="appSKey" class="control-label">{{ $t('a-endDevicesForm:appSKey') | capitalize }} *</label>
            <input id="appSKey"
                   key="appSKey"
                   v-model="appSKey"
                   v-validate="{ rules: { required: activation==='ABP', hexa32: true} }"
                   :data-vv-as="$t('a-endDevicesForm:appSKey')"
                   :class="{ 'is-invalid': errors.has('appSKey') }"
                   type="text"
                   name="appSKey"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('appSKey') }}</div>
          </div>
        </div>

        <div v-show="activation==='ABP'" class="form-row">
          <div class="form-group col-lg-6">
            <label for="devAddr" class="control-label">{{ $t('a-endDevicesForm:devAddr') | capitalize }} *</label>
            <input id="devAddr"
                   key="devAddr"
                   v-model="devAddr"
                   v-validate="{ rules: { required: activation==='ABP', hexa8: true} }"
                   :data-vv-as="$t('a-endDevicesForm:devAddr')"
                   :class="{ 'is-invalid': errors.has('devAddr') }"
                   type="text"
                   name="devAddr"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('devAddr') }}</div>
          </div>

          <div class="form-group col-lg-6">
            <label for="nwkSKey" class="control-label">{{ $t('a-endDevicesForm:nwkSKey') | capitalize }} *</label>
            <input id="nwkSKey"
                   key="nwkSKey"
                   v-model="nwkSKey"
                   v-validate="{ rules: { required: activation==='ABP', hexa32: true} }"
                   :data-vv-as="$t('a-endDevicesForm:nwkSKey')"
                   :class="{ 'is-invalid': errors.has('nwkSKey') }"
                   type="text"
                   name="nwkSKey"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('nwkSKey') }}</div>
          </div>
        </div>

        <div v-show="activation==='ABP'" class="form-row">
          <div class="form-group col-lg-3">
            <label for="fcntUp" class="control-label">{{ $t('a-endDevicesForm:fcntUp') | capitalize }}</label>
            <input id="fcntUp"
                   key="fcntUp"
                   v-model="fcntUp"
                   v-validate="'integer||between:0,4294967295'"
                   :data-vv-as="$t('a-endDevicesForm:fcntUp')"
                   :class="{ 'is-invalid': errors.has('fcntUp') }"
                   type="text"
                   name="fcntUp"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('fcntUp') }}</div>
          </div>

          <div class="form-group col-lg-3">
            <label for="fcntDown" class="control-label">{{ $t('a-endDevicesForm:fcntDown') | capitalize }}</label>
            <input id="fcntDown"
                   key="fcntDown"
                   v-model="fcntDown"
                   v-validate="'integer|between:0,4294967295'"
                   :data-vv-as="$t('a-endDevicesForm:fcntDown')"
                   :class="{ 'is-invalid': errors.has('fcntDown') }"
                   type="text"
                   name="fcntDown"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('fcntDown') }}</div>
          </div>
        </div>
      </template>

      <template v-if="step === 2">
        <h3 class="mt-3">{{ $t('a-endDevicesForm:rfParameters') | capitalize }}</h3>

        <div class="form-row">
          <div class="form-group col-lg-12">
            <input id="overrideRx"
                   v-model="overrideRx"
                   type="checkbox"
            >
            <label for="overrideRx" />
            <label for="overrideRx" class="control-label">{{ $t('a-endDevicesForm:overrideRxParameters') | capitalize }}</label>
          </div>
        </div>

        <div v-show="overrideRx" class="form-row">
          <div class="form-group col-lg-4">
            <label for="rx1Delay" class="control-label">{{ $t('a-endDevicesForm:rx1Delay') | capitalize }}</label>
            <input id="rx1Delay"
                   key="rx1Delay"
                   v-model="rx1Delay"
                   v-validate="'number|decimal:0|between:0,15'"
                   :data-vv-as="$t('a-endDevicesForm:rx1Delay')"
                   :class="{ 'is-invalid': errors.has('rx1Delay') }"
                   type="text"
                   name="rx1Delay"
                   class="form-control"
            >
            <small class="form-text text-muted">{{ $t('a-endDevicesForm:rx1DelayUnit') | capitalize }}</small>
            <div class="invalid-feedback">{{ errors.first('rx1Delay') }}</div>
          </div>

          <div class="form-group col-lg-4">
            <label for="rx2Dr" class="control-label">{{ $t('a-endDevicesForm:rx2Dr') | capitalize }}</label>
            <input id="rx2Dr"
                   key="rx2Dr"
                   v-model="rx2Dr"
                   v-validate="'number|decimal:0|between:0,15'"
                   :data-vv-as="$t('a-endDevicesForm:rx2Dr')"
                   :class="{ 'is-invalid': errors.has('rx2Dr') }"
                   type="text"
                   name="rx2Dr"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('rx2Dr') }}</div>
          </div>

          <div class="form-group col-lg-4">
            <label for="rx2Freq" class="control-label">{{ $t('a-endDevicesForm:rx2Freq') | capitalize }}</label>
            <input id="rx2Freq"
                   key="rx2Freq"
                   v-model="rx2Freq"
                   v-validate="'number|min_value:0'"
                   :data-vv-as="$t('a-endDevicesForm:rx2Freq')"
                   :class="{ 'is-invalid': errors.has('rx2Freq') }"
                   type="text"
                   name="rx2Freq"
                   class="form-control"
            >
            <small class="form-text text-muted">{{ $t('a-endDevicesForm:rx2FreqUnit') | capitalize }}</small>
            <div class="invalid-feedback">{{ errors.first('rx2Freq') }}</div>
          </div>
        </div>

        <div v-show="overrideRx" class="form-row">
          <div class="form-group col-lg-4">
            <label for="rx1DrOffset" class="control-label d-block">{{ $t('a-endDevicesForm:rx1DrOffset') | capitalize }}</label>
            <input id="rx1DrOffset"
                   key="rx1DrOffset"
                   v-model="rx1DrOffset"
                   v-validate="'number|decimal:0|between:0,7'"
                   :data-vv-as="$t('a-endDevicesForm:rx1DrOffset')"
                   :class="{ 'is-invalid': errors.has('rx1DrOffset') }"
                   type="text"
                   name="rx1DrOffset"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('rx1DrOffset') }}</div>
          </div>

          <div class="form-group col-lg-4">
            <label for="rxWindows" class="control-label d-block">{{ $t('a-endDevicesForm:rxWindows') | capitalize }}</label>
            <input id="rx1"
                   v-model="rxWindows"
                   :disabled="classType==='C'"
                   type="radio"
                   value="RX1"
                   name="rxWindows"
            >
            <label for="rx1" class="control-label">{{ $t('a-endDevicesForm:rxWindowsRX1') | capitalize }}</label>

            <input id="rx2"
                   v-model="rxWindows"
                   type="radio"
                   value="RX2"
                   name="rxWindows"
            >
            <label for="rx2" class="control-label">{{ $t('a-endDevicesForm:rxWindowsRX2') | capitalize }}</label>

            <input id="auto"
                   v-model="rxWindows"
                   type="radio"
                   value="AUTO"
                   name="rxWindows"
            >
            <label for="auto" class="control-label">{{ $t('a-endDevicesForm:rxWindowsAUTO') | capitalize }}</label>
          </div>

          <div v-if="canActivateDwellTime" class="form-group col-lg-4">
            <label class="control-label d-block">{{ $t('a-endDevicesForm:dwellTime') | capitalize }}</label>
            <input id="dwellTime"
                   v-model="dwellTime"
                   type="checkbox"
            >
            <label for="dwellTime" />
            <label for="dwellTime" class="control-label">{{ $t('a-endDevicesForm:dwellTimeEnable') | capitalize }}</label>
          </div>
        </div>

        <h5 v-show="overrideRx && cfListConf.count" class="mt-2">{{ $t('a-endDevicesForm:cfList') | capitalize }}</h5>
        <div :class="overrideRx ? 'd-flex' : 'd-none'" class="cfList">
          <div v-for="i in cfListConf.count" :key="cfListConf.label + i" class="form-group">
            <label :for="cfListConf.label + i" class="control-label">{{ $t('endDevices:cfList.' + cfListConf.label + i) | capitalize }}</label>
            <input :id="cfListConf.label + i"
                   :key="cfListConf.label + i"
                   v-model="cfList[i-1]"
                   v-validate="{rules: {
                     number: cfListConf.type === 'freq',
                     min_value: cfListConf.type === 'freq' ? '0' : false,
                     hexa4: cfListConf.type === 'mask'
                   }}"
                   :data-vv-as="$t('endDevices:cfList.' + cfListConf.label + i)"
                   :class="{ 'is-invalid': errors.has(cfListConf.label + i) }"
                   :name="cfListConf.label + i"
                   type="text"
                   class="form-control"
            >
            <small v-show="cfListConf.type === 'freq'" class="form-text text-muted">{{ $t('a-endDevicesForm:freqChanUnit', { freq: $t('endDevices:cfList.' + cfListConf.label + i) }) | capitalize }}</small>
            <small v-show="cfListConf.type === 'mask'" class="form-text text-muted">{{ $t('a-endDevicesForm:maskChanUnit', { mask: $t('endDevices:cfList.' + cfListConf.label + i) }) | capitalize }}</small>
            <div class="invalid-feedback">{{ errors.first(cfListConf.label + i) }}</div>
          </div>
        </div>

        <h5 v-show="overrideRx && classType === 'B'" class="mt-2">{{ $t('a-endDevicesForm:classB') | capitalize }}</h5>
        <div v-show="overrideRx && classType === 'B'" class="form-row">
          <div class="form-group col-lg-4">
            <label for="pingSlotDr" class="control-label">{{ $t('a-endDevicesForm:pingSlotDr') | capitalize }}</label>
            <select id="pingSlotDr"
                    key="pingSlotDr"
                    v-model="pingSlotDr"
                    :data-vv-as="$t('a-endDevicesForm:pingSlotDr')"
                    :class="{ 'is-invalid': errors.has('pingSlotDr') }"
                    name="pingSlotDr"
                    class="form-control"
            >
              <option v-for="dr in dataRates" :key="dr.value" :value="dr.value">{{ dr.label }}</option>
            </select>
            <div class="invalid-feedback">{{ errors.first('pingSlotDr') }}</div>
          </div>

          <div class="form-group col-lg-4">
            <label for="pingSlotFreq" class="control-label">{{ $t('a-endDevicesForm:pingSlotFreq') | capitalize }}</label>
            <input id="pingSlotFreq"
                   key="pingSlotFreq"
                   v-model="pingSlotFreq"
                   v-validate="'number|min_value:0'"
                   :data-vv-as="$t('a-endDevicesForm:pingSlotFreq')"
                   :class="{ 'is-invalid': errors.has('pingSlotFreq') }"
                   type="text"
                   name="pingSlotFreq"
                   class="form-control"
            >
            <small class="form-text text-muted">{{ $t('a-endDevicesForm:pingSlotFreqUnit') | capitalize }}</small>
            <div class="invalid-feedback">{{ errors.first('pingSlotFreq') }}</div>
          </div>
        </div>
      </template>

      <template v-if="step === 3">
        <h3 class="mt-3">{{ $t('a-endDevicesForm:geolocation') | capitalize }}</h3>

        <div class="form-group">
          <label for="geolocation" class="control-label d-block">{{ $t('a-endDevicesForm:geolocationType') | capitalize }} *</label>
          <input id="geolocManual"
                 v-model="geolocation"
                 v-validate="'required'"
                 :data-vv-as="$t('a-endDevicesForm:geolocationType')"
                 :class="{ 'is-invalid': errors.has('geolocation') }"
                 type="radio"
                 value="MANUAL"
                 name="geolocation"
                 class="form-control"
          >
          <label for="geolocManual" class="control-label">{{ $t('a-endDevicesForm:geolocationManual') | capitalize }}</label>

          <input id="geolocInherited"
                 v-model="geolocation"
                 :class="{ 'is-invalid': errors.has('geolocation') }"
                 type="radio"
                 value="INHERITED"
                 name="geolocation"
                 class="form-control"
          >
          <label for="geolocInherited" class="control-label">{{ $t('a-endDevicesForm:geolocationInherited') | capitalize }}</label>

          <div class="invalid-feedback">{{ errors.first('geolocation') }}</div>
        </div>

        <div v-show="geolocation==='MANUAL'" class="form-row">
          <div class="form-group col-lg-4">
            <label for="latitude" class="control-label">{{ $t('a-endDevicesForm:latitude') | capitalize }} *</label>
            <input id="latitude"
                   key="latitude"
                   v-model="latitude"
                   v-validate="{ rules: { required: geolocation==='MANUAL', number:true, between:[-90,90] } }"
                   :data-vv-as="$t('a-endDevicesForm:latitude')"
                   :class="{ 'is-invalid': errors.has('latitude') }"
                   type="text"
                   name="latitude"
                   class="form-control"
                   min="-90"
                   max="90"
            >
            <small class="form-text text-muted">{{ $t('a-endDevicesForm:latitudeUnit') | capitalize }}</small>
            <div class="invalid-feedback">{{ errors.first('latitude') }}</div>
          </div>

          <div class="form-group col-lg-4">
            <label for="longitude" class="control-label">{{ $t('a-endDevicesForm:longitude') | capitalize }} *</label>
            <input id="longitude"
                   key="longitude"
                   v-model="longitude"
                   v-validate="{ rules: { required: geolocation==='MANUAL', number:true, between:[-180,180] } }"
                   :data-vv-as="$t('a-endDevicesForm:longitude')"
                   :class="{ 'is-invalid': errors.has('longitude') }"
                   type="text"
                   name="longitude"
                   class="form-control"
                   min="-180"
                   max="180"
            >
            <small class="form-text text-muted">{{ $t('a-endDevicesForm:longitudeUnit') | capitalize }}</small>
            <div class="invalid-feedback">{{ errors.first('longitude') }}</div>
          </div>

          <div class="form-group col-lg-4">
            <label for="altitude" class="control-label">{{ $t('a-endDevicesForm:altitude') | capitalize }} *</label>
            <input id="altitude"
                   key="altitude"
                   v-model="altitude"
                   v-validate="{ rules: { required: geolocation==='MANUAL', number:true } }"
                   :data-vv-as="$t('a-endDevicesForm:altitude')"
                   :class="{ 'is-invalid': errors.has('altitude') }"
                   type="text"
                   name="altitude"
                   class="form-control"
            >
            <small class="form-text text-muted">{{ $t('a-endDevicesForm:altitudeUnit') | capitalize }}</small>
            <div class="invalid-feedback">{{ errors.first('altitude') }}</div>
          </div>
        </div>
      </template>
    </div>

    <div slot="modal-footer" class="w-100 text-right">
      <button v-if="step > 0" id="stepperPrevious" class="btn btn-flat float-left" @click="$refs.stepper.previous()">{{ $t('stepper:previous') | capitalize }}</button>
      <button id="stepperCancel" class="btn btn-flat" @click="cancel">{{ $t('stepper:cancel') | capitalize }}</button>
      <button v-if="step < steps.length-1" id="stepperNext" class="btn btn-flat" @click="$refs.stepper.next()">{{ $t('stepper:next') | capitalize }}</button>
      <button v-if="step === steps.length-1" id="stepperValidate" class="btn btn-flat" @click="createEndDevice()">{{ $t('stepper:validate') | capitalize }}</button>
    </div>
  </modal>
</template>

<script>
import { groupBy, orderBy, uniq, map, max, pick, find } from 'lodash-es'
import regions from '@/configuration/regions'
import to from '@/modules/await-to-js'
import eventBus from '@/modules/eventBus'
import regionMixin from '@/mixins/regionsMixin'

export default {
  mixins: [regionMixin],

  model: {
    prop: 'show',
    event: 'change'
  },

  props: {
    // Display or hide modal
    // To be used as v-model
    show: {
      type: Boolean,
      required: true
    }
  },

  data () {
    return {
      // Display or hide b-modal
      // It cannot change our props, so we need another variable
      visible: false,
      // Form
      clusterId: '',
      name: '',
      profile: '',
      classType: '',
      rfRegion: '',
      country: '',
      macVersion: '',
      regParamsRevision: '',
      activation: '',
      devEui: '',
      appEui: '',
      appKey: '',
      appSKey: '',
      devAddr: '',
      nwkSKey: '',
      devNonceCounter: false,
      fcntUp: 0,
      fcntDown: 0,
      overrideRx: false,
      rx1Delay: '',
      rx2Dr: '',
      rx2Freq: '',
      rxWindows: 'AUTO',
      rx1DrOffset: '',
      dwellTime: false,
      cfList: [],
      pingSlotDr: '',
      pingSlotFreq: '',
      adrEnabled: true,
      geolocation: '',
      latitude: '',
      longitude: '',
      altitude: '',
      // Stepper
      step: 0,
      steps: [
        { label: this.$t('a-endDevicesForm:identity') },
        { label: this.$t('a-endDevicesForm:activation') },
        { label: this.$t('a-endDevicesForm:rfParameters') },
        { label: this.$t('a-endDevicesForm:geolocation') }
      ]
    }
  },

  computed: {
    // Selected cluster
    selectedCluster () {
      return find(this.$store.getters.allClusters, c => c.id === this.clusterId) || {}
    },

    // Group clusters by customer for superAdmin
    groupedClusters () {
      // Order by customer name
      // Group by customer name to add customer in lists
      return groupBy(orderBy(this.$store.getters.allClusters, ['customer.name']), 'customer.name')
    },

    // DevNonceCounter only for 1.0 version
    isLoRaWan10 () {
      return this.macVersion && this.macVersion.indexOf('1.0') !== -1
    },

    countries () { return regions.countries[this.rfRegion] || [] },

    // AS923, AU915 1.0.2C and AU915 1.0.3A
    canActivateDwellTime () {
      if (this.rfRegion === 'AS923') return true
      if (this.rfRegion === 'AU915' && this.macVersion === '1.0.2' && this.regParamsRevision === 'C') return true
      if (this.rfRegion === 'AU915' && this.macVersion === '1.0.3') return true
      return false
    },

    cfListConf () {
      if (this.activation !== 'OTAA') return false
      if (this.rfRegion === '') return false
      if (this.macVersion === '') return false
      if (this.macVersion !== '1.0.1' && this.regParamsRevision === '') return false

      const cfListConf = regions.cfList[this.rfRegion] || {}
      // No cfList if not the minimum macVersion
      if (this.macVersion < cfListConf.minMac) return false
      // No regParamsRevision for macVersion 1.0.1 > return cfList
      if (this.macVersion === '1.0.1') return cfListConf
      // No cfList if not the minimum regParamsRevision
      if (this.macVersion === cfListConf.minMac && this.regParamsRevision < cfListConf.minRev) return false

      return cfListConf
    },

    cfListLabels () {
      const cfLabels = uniq(map(regions.cfList, c => c.label))
      const cfCounts = max(map(regions.cfList, c => c.count))
      const labels = []

      for (let l of cfLabels) {
        for (let i = 1; i <= cfCounts; i++) {
          labels.push(l + i)
        }
      }
      return labels
    }
  },

  watch: {
    // Synchronize show and visible
    // Use change event to update v-model
    show () {
      this.reset()
      this.visible = this.show

      if (this.show) {
        this.$store.dispatch('getAllClusters')
      }
    },
    visible () {
      this.$emit('change', this.visible)
    },

    rfRegion () {
      if (this.countries.length === 0) this.country = ''
    },

    // Reset OTAA/ABP values when switching (seems to be a bug with vee-validate and v-if, need to use v-show)
    activation (activation) {
      if (activation === 'OTAA') {
        this.appSKey = ''
        this.devAddr = ''
        this.nwkSKey = ''
        this.fcntUp = 0
        this.fcntDown = 0
      } else if (activation === 'ABP') {
        this.appEui = ''
        this.appKey = ''
        this.devNonceCounter = false
        this.cfList = []
      }
    },

    // Clear override rx parameters when unchecking
    overrideRx () {
      this.rx1Delay = ''
      this.rx2Dr = ''
      this.rx2Freq = ''
      this.rxWindows = 'AUTO'
      this.rx1DrOffset = ''
      this.dwellTime = false
      this.cfList = []
      this.pingSlotDr = ''
      this.pingSlotFreq = ''

      // Reset cfList validation errors
      for (let i = 0; i < this.cfListLabels.length; i++) {
        const err = find(this.$validator.fields.items, { name: this.cfListLabels[i] })
        if (err) {
          err.reset()
          this.$validator.errors.remove(this.cfListLabels[i])
        }
      }
    },

    // Reset geolocation values when switching
    geolocation (activation) {
      this.latitude = ''
      this.longitude = ''
      this.altitude = ''
    }
  },

  methods: {
    // Reset form
    async reset () {
      this.clusterId = ''
      this.name = ''
      this.profile = ''
      this.classType = ''
      this.rfRegion = ''
      this.country = ''
      this.macVersion = ''
      this.regParamsRevision = ''
      this.activation = ''
      this.devEui = ''
      this.appEui = ''
      this.appKey = ''
      this.appSKey = ''
      this.devAddr = ''
      this.nwkSKey = ''
      this.devNonceCounter = false
      this.fcntUp = 0
      this.fcntDown = 0
      this.overrideRx = false
      this.rx1Delay = ''
      this.rx2Dr = ''
      this.rx2Freq = ''
      this.rxWindows = 'AUTO'
      this.rx1DrOffset = ''
      this.dwellTime = false
      this.cfList = []
      this.pingSlotDr = ''
      this.pingSlotFreq = ''
      this.adrEnabled = true
      this.geolocation = ''
      this.latitude = ''
      this.longitude = ''
      this.altitude = ''
      this.step = 0

      // Reset form validation
      await this.$nextTick()
      this.$validator.reset()
    },

    cancel () {
      this.visible = false
      this.step = 0
      this.$emit('cancel')
    },

    async next () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      return true
    },

    // Send request if all inputs are validated
    async createEndDevice () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      // Fields to save
      const fields = [
        'name', 'profile', 'classType', 'rfRegion', 'macVersion', 'regParamsRevision',
        'activation', 'adrEnabled', 'geolocation'
      ]
      if (this.country) fields.push('country')
      if (this.activation === 'OTAA') fields.push('devEui', 'appEui', 'cfList')
      if (this.activation === 'OTAA' && this.isLoRaWan10) fields.push('devNonceCounter')
      if (this.activation === 'OTAA' && this.appKey) fields.push('appKey')
      if (this.activation === 'ABP') fields.push('devEui', 'appSKey', 'devAddr', 'nwkSKey', 'fcntUp', 'fcntDown')
      if (this.geolocation === 'MANUAL') fields.push('latitude', 'longitude', 'altitude')
      if (this.overrideRx) fields.push('rx1Delay', 'rx2Dr', 'rx2Freq', 'rxWindows', 'rx1DrOffset')
      if (this.overrideRx && this.canActivateDwellTime) fields.push('dwellTime')
      if (this.overrideRx && this.classType === 'B') fields.push('pingSlotDr', 'pingSlotFreq')

      const endDevice = pick(this, fields)
      endDevice.cluster = { id: this.clusterId }

      // Convert to integer
      if (this.activation === 'ABP') {
        endDevice.fcntUp = endDevice.fcntUp === '' ? 0 : Number(endDevice.fcntUp)
        endDevice.fcntDown = endDevice.fcntDown === '' ? 0 : Number(endDevice.fcntDown)
      }

      let error
      [error] = await to(this.$api.createEndDevice(this.$store.getters.application, this.devEui, endDevice))
      if (error) return

      this.visible = false
      // Success
      eventBus.$emit('message-success', this.$t('a-endDevicesForm:createSuccess'))
      // Reset form
      this.reset()
      // Refresh list
      this.$emit('success')
    }
  }
}
</script>

<style scoped lang="scss">
  .cfList {
    .form-group {
      flex: 1;
      margin-right: 5px;
      margin-left: 5px;

      &:first-child {
        margin-left: 0;
      }

      &:last-child {
        margin-right: 0;
      }
    }
  }
</style>
